import { BUSINESS_TYPE, TYPE_OF_DAMAGE } from '@/api/types';
import { DeadEnds, ExtendedSteps } from '@/features/v2/lib/wizard';
import {
  OWNER_ADDRESS_COMPANY_NAME,
  OWNER_ADDRESS_NAME,
} from '@/features/v2/summaryForms/components/InputAdress';
import {
  OWNER_BUILDING_NUMBER_COMPANY_NAME,
  OWNER_BUILDING_NUMBER_NAME,
} from '@/features/v2/summaryForms/components/InputBuildingNumber';
import {
  OWNER_CITY_COMPANY_NAME,
  OWNER_CITY_NAME,
} from '@/features/v2/summaryForms/components/InputCity';
import { OWNER_EMAIL_NAME } from '@/features/v2/summaryForms/components/InputEmail';
import { OWNER_FIRST_NAME_NAME } from '@/features/v2/summaryForms/components/InputFirstName';
import { OWNER_KRS_NAME } from '@/features/v2/summaryForms/components/InputKRS';
import { OWNER_LAST_NAME_NAME } from '@/features/v2/summaryForms/components/InputLastName';
import {
  OWNER_NIP_COMPANY_NAME,
  OWNER_NIP_NAME,
} from '@/features/v2/summaryForms/components/InputNip';
import { OWNER_PESEL_NAME } from '@/features/v2/summaryForms/components/InputPesel';
import { OWNER_PHONE_NAME } from '@/features/v2/summaryForms/components/InputPhone';
import {
  OWNER_ZIP_CODE_COMPANY_NAME,
  OWNER_ZIP_CODE_NAME,
} from '@/features/v2/summaryForms/components/InputPostalCode';
import { OWNER_REGON_NAME } from '@/features/v2/summaryForms/components/InputRegon';
import {
  StepSummary1,
  StepSummary2,
} from '@/features/v2/summaryForms/stepsSummary';
import {
  OWNER_ACCOUNT_NUMBER_NAME,
  OWNER_ACCOUNT_OWNER_FIRST_NAME,
  OWNER_ACCOUNT_OWNER_LAST_NAME,
} from '@/features/v2/summaryForms/surveyOne/BankInfoInputs';
import {
  OWNER_COMPANY_NAME,
  OWNER_TAX_OFFICE_NAME,
} from '@/features/v2/summaryForms/surveyTwo/Forms/CompaniesCompanyInputs';
import { StepComplementary } from '@/features/v2/vehicle-damage/complementaryInformation/step';
import {
  DAMAGE_RIGHTS_ASSIGNMENTS,
  DAMAGE_RIGHTS_ASSIGNMENTS_NAME_COMPLEMENTARY,
} from '@/features/v2/vehicle-damage/components/CheckboxGroupDamageRightsAssignments';
import {
  REPAIR_DOCUMENTS_PROVIDED_TO_IC,
  REPAIR_DOCUMENTS_PROVIDED_TO_IC_NAME_COMPLEMENTARY,
} from '@/features/v2/vehicle-damage/components/CheckboxGroupRepairDocumentsProvided';
import { BEFORE_ACCIDENT_REPAIRED_NAME_COMPLEMENTARY } from '@/features/v2/vehicle-damage/components/MultiSelectBeforeAccidentRepaired';
import { DAMAGE_BEFORE_ACCIDENT_NAME_COMPLEMENTARY } from '@/features/v2/vehicle-damage/components/MultiSelectDamageBeforeAccident';
import { ORIGINAL_PARTS_USED_FOR_REPAIR_NAME_COMPLEMENTARY } from '@/features/v2/vehicle-damage/components/OriginalPartsUsedForRepair';
import {
  ACCIDENT_IN_POLAND,
  ACCIDENT_IN_POLAND_NAME_COMPLEMENTARY,
} from '@/features/v2/vehicle-damage/components/RadioGroupAccidentInPoland';
import { ACCIDENT_REPORTED_TO_INSURER_NAME_COMPLEMENTARY } from '@/features/v2/vehicle-damage/components/RadioGroupAccidentReportedToInsurer';
import {
  AGREEMENT_OR_SETTLEMENT,
  AGREEMENT_OR_SETTLEMENT_NAME_COMPLEMENTARY,
} from '@/features/v2/vehicle-damage/components/RadioGroupAgreementOrSettlement';
import {
  BUSINESS_ACTIVITY,
  BUSINESS_ACTIVITY_NAME_COMPLEMENTARY,
} from '@/features/v2/vehicle-damage/components/RadioGroupBusinessActivity';
import { INSURER_PAY_COMPENSATION_NAME_COMPLEMENTARY } from '@/features/v2/vehicle-damage/components/RadioGroupInsurerPayCompensation';
import {
  UNREPAIRABLE_DAMAGES_BEFORE_ACCIDENT,
  UNREPAIRABLE_DAMAGES_BEFORE_ACCIDENT_NAME_COMPLEMENTARY,
} from '@/features/v2/vehicle-damage/components/RadioGroupNoRepairableDamage';
import {
  POLISH_PLATES,
  POLISH_PLATES_NAME_COMPLEMENTARY,
} from '@/features/v2/vehicle-damage/components/RadioGroupPolishPlates';
import {
  REPAIRS_BEFORE_ACCIDENT,
  REPAIRS_BEFORE_ACCIDENT_NAME_COMPLEMENTARY,
} from '@/features/v2/vehicle-damage/components/RadioGroupPreAccidentRepairs';
import {
  VAT_PAYER_STATUS,
  VAT_PAYER_STATUS_NAME_COMPLEMENTARY,
} from '@/features/v2/vehicle-damage/components/RadioGroupVATTaxpayerStatus';
import { VEHICLE_AS_FIXED_ASSET_NAME_COMPLEMENTARY } from '@/features/v2/vehicle-damage/components/RadioGroupVehicleAsFixedAsset';
import { STILL_VEHICLE_OWNER_NAME_COMPLEMENTARY } from '@/features/v2/vehicle-damage/components/RadioGroupVehicleOwner';
import {
  VEHICLE_USED_FOR_BUSINESS,
  VEHICLE_USED_FOR_BUSINESS_NAME_COMPLEMENTARY,
} from '@/features/v2/vehicle-damage/components/RadioGroupVehicleUsedForBusiness';
import {
  VEHICLE_VALUE_BEFORE_CRASH,
  VEHICLE_VALUE_BEFORE_CRASH_NAME_COMPLEMENTARY,
} from '@/features/v2/vehicle-damage/components/RadioGroupVehicleValueBeforeCrash';
import {
  WHEEL_SIDE,
  WHEEL_SIDE_NAME_COMPLEMENTARY,
} from '@/features/v2/vehicle-damage/components/RadioGroupWheelSide';
import {
  WHEEL_SIDE_FACTORY_INSTALLED_COMPLEMENTARY,
  WHEEL_SIDE_FOR_SURE,
} from '@/features/v2/vehicle-damage/components/RadioGroupWheelSideForSure';
import { VAT_DEDUCTION_TYPE_NAME_COMPLEMENTARY } from '@/features/v2/vehicle-damage/components/RadioVATDeductionPercentage';
import {
  AGREEMENT_OR_SETTLEMENT_CONCLUDED,
  AGREEMENT_OR_SETTLEMENT_CONCLUDED_NAME_COMPLEMENTARY,
} from '@/features/v2/vehicle-damage/components/SelectAgreementOrSettlementConcluded';
import { BUSINESS_TYPE_NAME_COMPLEMENTARY } from '@/features/v2/vehicle-damage/components/SelectBusinessType';
import {
  CAR_CONDITION,
  CAR_CONDITION_NAME_COMPLEMENTARY,
} from '@/features/v2/vehicle-damage/components/SelectCarCondition';
import { INSURANCE_COMPANY_NAME_COMPLEMENTARY } from '@/features/v2/vehicle-damage/components/SelectInsuranceCompany';
import { EnumLike } from '@/types';

import { i18n } from '@lingui/core';

const stepComplementaryProgressPoints = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: Record<any, any>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  additionalInfo: Record<any, any>
) => {
  const typeOfDamage = additionalInfo.typeOfDamage;
  const isVehicleSold = additionalInfo.isVehicleSold;

  const points = [
    ACCIDENT_REPORTED_TO_INSURER_NAME_COMPLEMENTARY,
    INSURANCE_COMPANY_NAME_COMPLEMENTARY,
    ACCIDENT_IN_POLAND_NAME_COMPLEMENTARY,
    CAR_CONDITION_NAME_COMPLEMENTARY,
    WHEEL_SIDE_NAME_COMPLEMENTARY,
    INSURER_PAY_COMPENSATION_NAME_COMPLEMENTARY,

    AGREEMENT_OR_SETTLEMENT_NAME_COMPLEMENTARY,

    // Survey Three
    UNREPAIRABLE_DAMAGES_BEFORE_ACCIDENT_NAME_COMPLEMENTARY,
    REPAIRS_BEFORE_ACCIDENT_NAME_COMPLEMENTARY,
    BUSINESS_ACTIVITY_NAME_COMPLEMENTARY,

    // Survey Four
    REPAIR_DOCUMENTS_PROVIDED_TO_IC_NAME_COMPLEMENTARY,
    DAMAGE_RIGHTS_ASSIGNMENTS_NAME_COMPLEMENTARY,
  ];

  if (
    typeOfDamage === TYPE_OF_DAMAGE.FULL_OC ||
    typeOfDamage === TYPE_OF_DAMAGE.PART_OC
  ) {
    points.push(POLISH_PLATES_NAME_COMPLEMENTARY);
  }

  if (
    typeOfDamage === TYPE_OF_DAMAGE.FULL_AC ||
    typeOfDamage === TYPE_OF_DAMAGE.FULL_OC
  ) {
    points.push(VEHICLE_VALUE_BEFORE_CRASH_NAME_COMPLEMENTARY);
  }

  if (value[WHEEL_SIDE_NAME_COMPLEMENTARY] === WHEEL_SIDE.LEFT) {
    points.push(WHEEL_SIDE_FACTORY_INSTALLED_COMPLEMENTARY);
  }

  if (
    value[AGREEMENT_OR_SETTLEMENT_NAME_COMPLEMENTARY] ===
    AGREEMENT_OR_SETTLEMENT.YES
  ) {
    points.push(AGREEMENT_OR_SETTLEMENT_CONCLUDED_NAME_COMPLEMENTARY);
  }

  // Survey Three

  if (!isVehicleSold) {
    points.push(STILL_VEHICLE_OWNER_NAME_COMPLEMENTARY);
  }

  if (
    value[UNREPAIRABLE_DAMAGES_BEFORE_ACCIDENT_NAME_COMPLEMENTARY] ===
    UNREPAIRABLE_DAMAGES_BEFORE_ACCIDENT.YES
  ) {
    points.push(DAMAGE_BEFORE_ACCIDENT_NAME_COMPLEMENTARY);
  }

  if (
    value[REPAIRS_BEFORE_ACCIDENT_NAME_COMPLEMENTARY] ===
    REPAIRS_BEFORE_ACCIDENT.YES
  ) {
    points.push(ORIGINAL_PARTS_USED_FOR_REPAIR_NAME_COMPLEMENTARY);
    points.push(BEFORE_ACCIDENT_REPAIRED_NAME_COMPLEMENTARY);
  }

  if (value[BUSINESS_ACTIVITY_NAME_COMPLEMENTARY] === BUSINESS_ACTIVITY.YES) {
    points.push(VEHICLE_USED_FOR_BUSINESS_NAME_COMPLEMENTARY);

    if (
      value[VEHICLE_USED_FOR_BUSINESS_NAME_COMPLEMENTARY] ===
      VEHICLE_USED_FOR_BUSINESS.YES
    ) {
      points.push(BUSINESS_TYPE_NAME_COMPLEMENTARY);
    }

    if (value[BUSINESS_TYPE_NAME_COMPLEMENTARY]) {
      points.push(VEHICLE_AS_FIXED_ASSET_NAME_COMPLEMENTARY);
    }

    if (value[VEHICLE_AS_FIXED_ASSET_NAME_COMPLEMENTARY]) {
      points.push(VAT_PAYER_STATUS_NAME_COMPLEMENTARY);
    }

    if (value[VAT_PAYER_STATUS_NAME_COMPLEMENTARY] === VAT_PAYER_STATUS.YES) {
      points.push(VAT_DEDUCTION_TYPE_NAME_COMPLEMENTARY);
    }
  }

  return points;
};

export const VehicleDamageSchemaDeadEndsStepOneComplementary = {
  noPolishInsurance: {
    condition: (val) =>
      val[POLISH_PLATES_NAME_COMPLEMENTARY] === POLISH_PLATES.NO &&
      val[ACCIDENT_IN_POLAND_NAME_COMPLEMENTARY] === ACCIDENT_IN_POLAND.NO,
    returnToStep: 1,
  },
  goingToBeSoldDamagedCarCondition: {
    condition: (val) =>
      val[CAR_CONDITION_NAME_COMPLEMENTARY] ===
      CAR_CONDITION.GOING_TO_BE_SOLD_DAMAGED,
    returnToStep: 1,
  },
  AgreementOrSettlementConcluded: {
    condition: (val) =>
      val[AGREEMENT_OR_SETTLEMENT_CONCLUDED_NAME_COMPLEMENTARY]?.some(
        (item: EnumLike) =>
          item.value === AGREEMENT_OR_SETTLEMENT_CONCLUDED.VEHICLE_REPAIR_COSTS
      ),
    returnToStep: 2,
  },
  radioGroupVehicleValueBeforeCrash: {
    condition: (val) =>
      val[VEHICLE_VALUE_BEFORE_CRASH_NAME_COMPLEMENTARY] ===
      VEHICLE_VALUE_BEFORE_CRASH.UNDER_10K,
    returnToStep: 1,
  },
  carSold: {
    condition: (val) =>
      val[CAR_CONDITION_NAME_COMPLEMENTARY] ===
        CAR_CONDITION.GOING_TO_BE_SOLD_DAMAGED ||
      val[CAR_CONDITION_NAME_COMPLEMENTARY] === CAR_CONDITION.SOLD_DAMAGED ||
      val[CAR_CONDITION_NAME_COMPLEMENTARY] === CAR_CONDITION.SOLD_REPAIRED,
    returnToStep: 1,
  },
  fullAgreementOrSettlementConcluded: {
    condition: (val) =>
      val[AGREEMENT_OR_SETTLEMENT_CONCLUDED_NAME_COMPLEMENTARY]?.some(
        (item: EnumLike) =>
          item.value === AGREEMENT_OR_SETTLEMENT_CONCLUDED.VEHICLE_REPAIR_COSTS
      ),
    returnToStep: 2,
  },
  wheelNotOnTheLeft: {
    condition: (val) =>
      (val[WHEEL_SIDE_NAME_COMPLEMENTARY] &&
        val[WHEEL_SIDE_NAME_COMPLEMENTARY] !== WHEEL_SIDE.LEFT) ||
      (val[WHEEL_SIDE_FACTORY_INSTALLED_COMPLEMENTARY] &&
        val[WHEEL_SIDE_FACTORY_INSTALLED_COMPLEMENTARY] !==
          WHEEL_SIDE_FOR_SURE.YES),
    returnToStep: 0,
  },
  withAdditionalDocuments: {
    condition: (val) => {
      const documentsProvided =
        val[REPAIR_DOCUMENTS_PROVIDED_TO_IC_NAME_COMPLEMENTARY];
      const damageRightsAssignments =
        val[DAMAGE_RIGHTS_ASSIGNMENTS_NAME_COMPLEMENTARY];

      return (
        documentsProvided?.some(
          (item: EnumLike) =>
            item.value === REPAIR_DOCUMENTS_PROVIDED_TO_IC.INVOICES ||
            item.value === REPAIR_DOCUMENTS_PROVIDED_TO_IC.RECEIPTS ||
            item.value === REPAIR_DOCUMENTS_PROVIDED_TO_IC.OTHER_DOCUMENTS
        ) ||
        damageRightsAssignments?.some(
          (item: EnumLike) =>
            item.value === DAMAGE_RIGHTS_ASSIGNMENTS.DAMAGE_AOB ||
            item.value ===
              DAMAGE_RIGHTS_ASSIGNMENTS.DAMAGE_RIGHTS_AUTHORIZATIONS ||
            item.value === DAMAGE_RIGHTS_ASSIGNMENTS.DAMAGE_RIGHTS_POA
        )
      );
    },

    returnToStep: 0,
  },
  civilPartnership: {
    condition: (val) =>
      val[BUSINESS_TYPE_NAME_COMPLEMENTARY] === BUSINESS_TYPE.SC,
    returnToStep: 0,
  },
} as const satisfies DeadEnds;

const stepOneProgressPoints = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: Record<any, any>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  additionalInfo: Record<any, any>
) => {
  const points = [OWNER_ACCOUNT_NUMBER_NAME];

  if (
    !!additionalInfo?.recipientType &&
    additionalInfo?.recipientType !== BUSINESS_TYPE.JDG
  ) {
    points.push(OWNER_COMPANY_NAME);
  }

  if (
    !additionalInfo?.recipientType ||
    additionalInfo?.recipientType === BUSINESS_TYPE.JDG
  ) {
    points.push(OWNER_ACCOUNT_OWNER_FIRST_NAME, OWNER_ACCOUNT_OWNER_LAST_NAME);
  }

  return points;
};

const stepTwoProgressPoints = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: Record<any, any>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  additionalInfo: Record<any, any>
) => {
  // common points
  const points = [
    OWNER_ADDRESS_NAME,
    OWNER_BUILDING_NUMBER_NAME,
    OWNER_ZIP_CODE_NAME,
    OWNER_CITY_NAME,
    OWNER_EMAIL_NAME,
    OWNER_PHONE_NAME,
    OWNER_FIRST_NAME_NAME,
    OWNER_LAST_NAME_NAME,
    OWNER_PESEL_NAME,
  ];

  if (
    additionalInfo?.recipientType === BUSINESS_TYPE.JDG ||
    !additionalInfo?.recipientType
  ) {
    if (additionalInfo?.recipientType === BUSINESS_TYPE.JDG) {
      points.push(OWNER_NIP_NAME);
    }

    // if without_pesel is true, then we don't need PESEL
    if (value?.owner?.without_pesel) {
      points.splice(points.indexOf(OWNER_NIP_NAME), 1);
    }

    // if without_pesel is true, then we don't need PESEL
    if (value?.owner?.without_pesel) {
      points.splice(points.indexOf(OWNER_PESEL_NAME), 1);
    }

    // if email have value then we don't need phone
    if (value?.owner?.email) {
      points.splice(points.indexOf(OWNER_PHONE_NAME), 1);
    }

    // if phone have value then we don't need email
    if (value?.owner?.phone) {
      points.splice(points.indexOf(OWNER_EMAIL_NAME), 1);
    }

    if (additionalInfo?.recipientType === null) {
      points.push(OWNER_TAX_OFFICE_NAME);
      if (value.coowners && value.coowners.length > 0) {
        value.coowners.forEach((_: string, index: number) => {
          points.push(`coowners.${index}.tax_office`);
        });
      }
    }

    if (value.coowners && value.coowners.length > 0) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      value.coowners.forEach((coowner: Record<any, any>, index: number) => {
        points.push(
          `coowners.${index}.first_name`,
          `coowners.${index}.last_name`,
          `coowners.${index}.phone`,
          `coowners.${index}.email`,
          `coowners.${index}.address`,
          `coowners.${index}.building_number`,
          `coowners.${index}.zip_code`,
          `coowners.${index}.city`
        );
        additionalInfo?.recipientType === null &&
          !coowner?.without_pesel &&
          points.push(`coowners.${index}.pesel`);

        if (coowner?.email) {
          points.splice(points.indexOf(`coowners.${index}.phone`), 1);
        }

        // if co-owner phone have value then we don't need co-owner email
        if (coowner?.phone) {
          points.splice(points.indexOf(`coowners.${index}.email`), 1);
        }
      });
    }
  }

  // companies
  if (
    additionalInfo?.recipientType &&
    additionalInfo?.recipientType !== BUSINESS_TYPE.JDG
  ) {
    points.push(
      OWNER_COMPANY_NAME,
      OWNER_NIP_COMPANY_NAME,
      OWNER_KRS_NAME,
      OWNER_ADDRESS_COMPANY_NAME,
      OWNER_BUILDING_NUMBER_COMPANY_NAME,
      OWNER_ZIP_CODE_COMPANY_NAME,
      OWNER_CITY_COMPANY_NAME,
      OWNER_REGON_NAME
    );
    if (value?.owner?.without_pesel) {
      points.splice(points.indexOf('owner.pesel'), 1);
    }

    // if email have value then we don't need phone
    if (value?.owner?.email) {
      points.splice(points.indexOf(OWNER_PHONE_NAME), 1);
    }

    // if phone have value then we don't need email
    if (value?.owner?.phone) {
      points.splice(points.indexOf(OWNER_EMAIL_NAME), 1);
    }

    if (value.coowners && value.coowners.length > 0) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      value.coowners.forEach((coowner: Record<any, any>, index: number) => {
        points.push(
          `coowners.${index}.first_name`,
          `coowners.${index}.last_name`,
          `coowners.${index}.phone`,
          `coowners.${index}.email`,
          `coowners.${index}.address`,
          `coowners.${index}.building_number`,
          `coowners.${index}.zip_code`,
          `coowners.${index}.city`
        );
        // if without_pesel is true, then we don't need PESEL
        if (coowner?.without_pesel) {
          points.push(`coowners.${index}.pesel`);
        }
      });
    }
  }

  return points;
};

export const SummarySchema = [
  {
    label: i18n.t('Uzupełnienie informacji'),
    component: StepComplementary,
    progressPoints: stepComplementaryProgressPoints,
    deadEnds: VehicleDamageSchemaDeadEndsStepOneComplementary,
    visibleInProgressBar: (_, isComplementary) => {
      return isComplementary;
    },
    chatMessage: (userName, _, amount) => {
      return (
        <>
          {i18n.t('Świetnie')} {userName},{' '}
          {i18n.t(
            'propozycja została zaakceptowana! Uzupełnij informacje i uzyskaj'
          )}{' '}
          {amount?.toLocaleString('PL', {
            style: 'currency',
            currency: 'PLN',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }) ?? 0}{' '}
          {i18n.t('dopłaty.')}
        </>
      );
    },
  },
  {
    label: i18n.t('Dane do przelewu'),
    component: StepSummary1,
    progressPoints: stepOneProgressPoints,
    visibleInProgressBar: (step) => {
      return step >= 4;
    },
    lastStep: () => {
      return {
        lastStep: 1,
        additionalStep: false,
      };
    },
    completeValidation: () => true,
    chatMessage: (userName, _, amount) => {
      return (
        <>
          {i18n.t('Świetnie')} {userName},{' '}
          {i18n.t(
            'propozycja została zaakceptowana! Podaj dane do przelewu i uzyskaj'
          )}{' '}
          {amount?.toLocaleString('PL', {
            style: 'currency',
            currency: 'PLN',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }) ?? 0}{' '}
          {i18n.t('dopłaty.')}
        </>
      );
    },
  },
  {
    label: i18n.t('Współwłaściciele'),
    component: StepSummary2,
    progressPoints: stepTwoProgressPoints,
    visibleInProgressBar: (step) => {
      return step >= 4;
    },
    backButton: () => {
      return true;
    },
    lastStep: () => {
      return {
        lastStep: 1,
        additionalStep: false,
      };
    },
    completeValidation: () => true,
    chatMessage: (_) => {
      return i18n.t(
        'Prosimy o podanie danych właściciela i współwłaścicieli pojazdu. Twoje wsparcie w tym zakresie umożliwi nam sprawną finalizację umowy.'
      );
    },
  },
  {
    label: i18n.t('Podpisanie dokumentów'),
    visibleInProgressBar: (step) => {
      return step >= 4;
    },
  },
  {
    label: i18n.t('Weryfikacja dokumentów'),
    visibleInProgressBar: (step) => {
      return step >= 4;
    },
  } as Partial<ExtendedSteps>,
] as ExtendedSteps[];
